// components/TourManager.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import { useAppContext } from '../context/AppContext';
import 'driver.js/dist/driver.css';

const TourManager = () => {
  const { activeTour, setActiveTour, markTaskDoneTour } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const driverRef = useRef(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Tour steps configuration - ONLY CHANGE: Remove onComplete from prompt form step
  const tourSteps = {
    1: [ // Create Prompt
      {
        path: "/get-started",
        element: "#sidebar-prompt",
        title: "Step 1: Navigate to Prompts",
        description: "First, let's go to the Prompt Management section. Click on 'Prompts' in the sidebar.",
        nextPath: "/prompts"
      },
      {
        path: "/prompts",
        element: "#create-prompt-btn",
        title: "Create Your First Prompt",
        description: "Great! Now click this button to start creating your first prompt.",
        nextPath: "/create-new-prompt"
      },
      {
        path: "/create-new-prompt",
        element: "#prompt-form",
        title: "Prompt Creation Form",
        description: "Fill in the details for your prompt here. Give it a name and description.",
      }
    ],
    2: [ // Create Agent
      {
        path: "/get-started",
        element: "#sidebar-agent",
        title: "Step 1: Navigate to Agents",
        description: "Let's create an agent! Click on 'Agents' in the sidebar.",
        nextPath: "/agents"
      },
      {
        path: "/agents",
        element: "#create-agent-btn",
        title: "Create Your First Agent",
        description: "Now click here to create a new agent that will use your prompts.",
        nextPath: "/create-new-agent"
      },
      {
        path: "/create-new-agent",
        element: "#agent-form",
        title: "Agent Configuration",
        description: "Configure your agent settings here. Select a prompt and set up its behavior.",
      }
    ],
    3: [ // Make a Call
      {
        path: "/get-started",
        element: "#sidebar-calls",
        title: "Step 1: Navigate to Calls",
        description: "Click on the call list in the side bar in the sidebar.",
        nextPath: "/call-list"
      },
      {
        path: "/call-list",
        element: "#make-call-btn",
        title: "Step 2: Make your first call",
        description: "Time to make your first call! Click on 'Make Call' button.",
        nextPath: "/make-call"
      },
      {
        path: "/make-call",
        element: "#make-call-form",
        title: "Start Your First Call",
        description: "Select agent and enter phone number to make your first call.",
      }
    ],
    4: [ // Choose a Plan
      {
        path: "/get-started",
        element: "#sidebar-plans",
        title: "Step 1: Navigate to Pricing",
        description: "Let's choose your plan! Click on 'Pricing' in the sidebar.",
        nextPath: "/pricing-plans"
      },
      {
        path: "/pricing-plans",
        element: "#plan-card",
        title: "Choose Your Plan",
        description: "Select the plan that best fits your needs. You can upgrade anytime.",
        onComplete: true
      }
    ]
  };

  // NEW: Function to highlight sidebar items
  const highlightSidebarItem = (itemId) => {
    // Remove highlight from all sidebar items
    const sidebarItems = ['sidebar-prompt', 'sidebar-agent', 'sidebar-calls', 'sidebar-plans'];
    sidebarItems.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.style.backgroundColor = '';
        element.style.fontWeight = '';
        element.classList.remove('active');
      }
    });
    
    // Highlight the current sidebar item
    const currentItem = document.getElementById(itemId);
    if (currentItem) {
      currentItem.style.backgroundColor = 'rgba(13, 110, 253, 0.1)';
      currentItem.style.fontWeight = '600';
      currentItem.classList.add('active');
    }
  };

  // NEW: Function to setup click listeners for tour elements
  const setupElementClickListener = (element, taskId, stepIndex, step) => {
    if (!element) return;

    const handleElementClick = () => {
      console.log(`🎯 Element clicked: ${step.element}, moving to next step`);
      
      // Clean up driver.js
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }

      // Remove the click listener
      element.removeEventListener('click', handleElementClick);

      // Handle navigation or next step
      if (step.nextPath) {
        setIsNavigating(true);
        setActiveTour({ taskId, stepIndex: stepIndex + 1 });
        navigate(step.nextPath);
      } else {
        // For form steps or final steps, move to next step
        setActiveTour({ taskId, stepIndex: stepIndex + 1 });
      }
    };

    // Add click listener
    element.addEventListener('click', handleElementClick);
    
    // Return cleanup function
    return () => {
      element.removeEventListener('click', handleElementClick);
    };
  };

  useEffect(() => {
    console.log('📍 TourManager: Location changed to', location.pathname);
    console.log('📍 TourManager: Active tour', activeTour);

    if (activeTour && activeTour.taskId !== undefined && activeTour.stepIndex !== undefined) {
      const timer = setTimeout(() => {
        handleTourStep(activeTour.taskId, activeTour.stepIndex);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, activeTour]);

  const waitForElement = (selector, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      const checkElement = () => {
        const element = document.querySelector(selector);
        if (element) {
          console.log(`✅ Element found: ${selector}`);
          resolve(element);
          return;
        }
        
        if (Date.now() - startTime >= timeout) {
          reject(new Error(`Element ${selector} not found after ${timeout}ms`));
          return;
        }
        
        setTimeout(checkElement, 100);
      };
      
      checkElement();
    });
  };

  const handleTourStep = async (taskId, stepIndex) => {
    console.log(`🔄 handleTourStep called: task ${taskId}, step ${stepIndex}, path: ${location.pathname}`);
    
    const steps = tourSteps[taskId];
    if (!steps || steps.length === 0) {
      console.log(`❌ No steps found for task ${taskId}`);
      return;
    }

    // Check if this is the final step (no more steps)
    if (stepIndex >= steps.length) {
      console.log(`✅ All steps completed for task ${taskId}`);
      markTaskDoneTour(taskId);
      return;
    }

    const step = steps[stepIndex];
    console.log('📝 Current step config:', step);

    // Highlight sidebar item if this step involves sidebar
    if (step.element && step.element.includes('sidebar-')) {
      const sidebarItemId = step.element.replace('#', '');
      highlightSidebarItem(sidebarItemId);
    }

    // Check if we're on the correct path for this step
    if (step.path && location.pathname !== step.path) {
      console.log(`🧭 Need to navigate from ${location.pathname} to ${step.path}`);
      setIsNavigating(true);
      navigate(step.path);
      return;
    }

    // We're on the correct path, start the tour step
    try {
      // Wait for element to be available
      if (step.element) {
        await waitForElement(step.element);
      }

      // Clean up any existing driver
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }

      // Additional delay to ensure everything is ready
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log(`🚀 Starting driver.js tour for step ${stepIndex}`);

      // Get the actual element
      const targetElement = document.querySelector(step.element);
      
      // NEW: Setup click listener for the target element (skip for form elements)
      if (targetElement && !step.element.includes('form')) {
        const cleanupClickListener = setupElementClickListener(targetElement, taskId, stepIndex, step);
      }

      // Create new driver instance - WITHOUT buttons
      const drv = driver({
        showProgress: true,
        progressText: `Step ${stepIndex + 1} of ${steps.length}`,
        allowClose: true,
        overlayClickToExit: false,
        overlayOpacity: 0.5,
        animate: true,
        // REMOVED buttons - user will click actual elements
        showButtons: [], // No buttons at all
        steps: [
          {
            element: step.element,
            popover: {
              title: step.title,
              description: step.description,
              side: 'bottom',
              align: 'start'
            }
          }
        ],
        onCloseClick: () => {
          drv.destroy();
          driverRef.current = null;
          setActiveTour(null);
        },
        onDestroyed: () => {
          drv.destroy();
          driverRef.current = null;
          setActiveTour(null);
        }
      });

      driverRef.current = drv;
      drv.drive();

    } catch (error) {
      console.error('💥 Tour step error:', error);
      // If element not found, try to proceed to next step after a delay
      setTimeout(() => {
        if (step.nextPath) {
          setIsNavigating(true);
          setActiveTour({ taskId, stepIndex: stepIndex + 1 });
          navigate(step.nextPath);
        } else {
          setActiveTour({ taskId, stepIndex: stepIndex + 1 });
        }
      }, 1000);
    }
  };

  return null;
};

export default TourManager;